import React, {useState} from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import CardBase from "connectui/component/display/card/CardBase";
import SvgIcon from "connectui/component/display/SvgIcon";
import Text from "connectui/component/display/Text";
import ButtonText from "connectui/component/input/button/ButtonText";
import LightButtonBase from "connectui/component/input/button/base/LightButtonBase";
import OutlinedButtonBase from "connectui/component/input/button/base/OutlinedButtonBase";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Tooltip from "connectui/component/display/Tooltip";
import EmptyButtonBase from "connectui/component/input/button/base/EmptyButtonBase";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import {ReactComponent as PhoneIcon} from "connectui/icons/phone-card.svg";
import {ReactComponent as MobileIcon} from "connectui/icons/mobile.svg";
import {ReactComponent as EditIcon} from "connectui/icons/edit.svg";
import {ReactComponent as PhoneMiniIcon} from "connectui/icons/phone-mini.svg";
import {ReactComponent as RedirectPhoneMiniIcon} from "connectui/icons/redirect-phone-mini.svg";
import {ReactComponent as WhatsAppMiniIcon} from "connectui/icons/whatsapp-mini.svg";
import {ReactComponent as MessageMiniIcon} from "connectui/icons/message-mini.svg";
import StatusChip from "../chips/StatusChip";
import ServiceInfoDialog from "components/Dialogs/ServiceInfoContent";
import {addonServices} from "configuration/enums";
import {postChannels} from "redux/actions/accountsActions";
import {messagingSettingsStageEnum} from "../../BaseMessagingSettingsPanel";
import Skeleton from "../../../../../../connectui/component/display/Skeleton";

const managedPhoneType = (phone) => {
  if (
    phone.active &&
    phone.capabilities.some((c) => c.toLowerCase() === "whatsapp") &&
    !phone.capabilities.some(
      (c) => c.toLowerCase() === "call" || c.toLowerCase() === "sms",
    )
  ) {
    return "whatsapp";
  }
  return phone.active ? "managed" : "display";
};

const PhoneCardActionRow = ({
  user_profile,
  setStage,
  setSubscribeDialogOpen,
}) =>
  user_profile?.connected_services?.includes("sms") ? (
    <ButtonText
      sx={{flex: 1}}
      typographyVariant="textSM"
      weight="semibold"
      color="primary"
      onClick={() => setStage({id: messagingSettingsStageEnum.phone, data: {}})}
      buttonBase={LightButtonBase}
      label="Add Phone"
    />
  ) : (
    <>
      <ButtonText
        sx={{flex: 1}}
        typographyVariant="textSM"
        weight="semibold"
        color="primary"
        onClick={() =>
          setStage({
            id: messagingSettingsStageEnum.phoneEdit,
            data: {type: "display_only"},
            overrideBackToStageId: messagingSettingsStageEnum.settings,
          })
        }
        buttonBase={LightButtonBase}
        label="Add display phone"
      />
      <ButtonText
        sx={{flex: 1}}
        typographyVariant="textSM"
        weight="semibold"
        color="gray700"
        onClick={() => setSubscribeDialogOpen(true)}
        buttonBase={OutlinedButtonBase}
        label="Enable SMS"
      />
    </>
  );

// TODO: Ignore whatsapp from these
const ManagedPhoneItem = ({phone, theme, setStage}) => (
  <FlexContainer alignItems="center" gap={theme.spacing(2)}>
    <SvgIcon component={MobileIcon} sx={{width: 16, height: 16}} />
    <Text variant="textMD" sx={{paddingRight: theme.spacing(1)}}>
      {phone.phone}
    </Text>
    <Tooltip
      tooltipContent={
        <Text variant="textXS">
          {phone.active
            ? "This number is active and will appear in the inbox."
            : "This number is display-only."}
        </Text>
      }
    >
      <StatusChip
        text={phone.active ? "Active" : "Display Only"}
        type={phone.active ? "success" : "blue"}
      />
    </Tooltip>
    <ButtonIcon
      buttonBase={EmptyButtonBase}
      icon={
        <SvgIcon
          component={EditIcon}
          sx={{color: theme.colors.primary, width: 16, height: 16}}
        />
      }
      sx={{padding: theme.spacing(0.5), marginLeft: theme.spacing(1)}}
      onClick={() =>
        setStage({
          id: messagingSettingsStageEnum.phoneEdit,
          data: {type: phone.active ? "active" : "display_only", phone: phone},
          overrideBackToStageId: messagingSettingsStageEnum.settings,
        })
      }
    />
  </FlexContainer>
);

const ManagedPhoneCapabilities = ({phone, theme}) => (
  <FlexContainer alignItems="center" gap={theme.spacing(3)}>
    {[
      ["SMS", "sms", MessageMiniIcon],
      ["Voice", "call", PhoneMiniIcon],
      ["WhatsApp", "whatsapp", WhatsAppMiniIcon],
    ].map(([text, capability, icon]) => (
      <StatusChip
        key={capability}
        text={text}
        type={
          phone.capabilities.some((c) => c.toLowerCase() === capability)
            ? "success"
            : "gray"
        }
        icon={icon}
      />
    ))}
  </FlexContainer>
);

const RedirectCapabilities = ({phone, theme}) =>
  (phone.redirect_phone &&
    managedPhoneType(phone) !== "whatsapp" &&
    phone.capabilities.map((c) => c.toLowerCase()).includes("call") && (
      <StatusChip
        text={`Redirect Phone: ${phone.redirect_phone}`}
        type={"gray"}
        icon={RedirectPhoneMiniIcon}
      />
    )) || <></>;

const PhoneCard = ({setStage, data, ...props}) => {
  const {theme} = useTheme();
  const {t} = useTranslation();

  const userProfileLoading = useSelector(
    (state) => state.defaultReducer.loading.profile,
  );

  const dispatch = useDispatch();
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const isSmsEnabled = user_profile?.connected_services?.includes("sms");
  const managedPhones = user_profile?.managed_phones;

  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const subscribe = () => {
    setSubscriptionLoading(true);
    dispatch(
      postChannels(
        {
          enso_key: user_profile?.enso_key,
          data: {service: "sms", provider: "twilio", options: {}},
        },
        (response) => {
          if (response.options.success) {
            user_profile.connected_services = [
              ...(user_profile.connected_services || []),
              "sms",
            ];
          } else if (response.options.message === "pay") {
            window.location.href = "/admin/settings/billing/:redirect";
          }
          setSubscriptionLoading(false);
          setSubscribeDialogOpen(false);
        },
        () => {
          setSubscriptionLoading(false);
          setSubscribeDialogOpen(false);
        },
      ),
    );
  };

  const getCardContent = () => {
    return (
      <FlexContainer
        flexDirection="column"
        alignItems="flex-start"
        gap={theme.spacing(3)}
      >
        {managedPhones
          ?.filter((p) => managedPhoneType(p) !== "whatsapp")
          .map((p) => (
            <FlexContainer
              flexDirection="column"
              key={p.phone}
              gap={theme.spacing(1.5)}
            >
              <ManagedPhoneItem phone={p} theme={theme} setStage={setStage} />
              <ManagedPhoneCapabilities phone={p} theme={theme} />
              <RedirectCapabilities phone={p} theme={theme} />
            </FlexContainer>
          ))}
      </FlexContainer>
    );
  };

  const getActionRow = () => {
    if (userProfileLoading) {
      return <Skeleton width={"150px"} height={"36px"} />;
    }

    return (
      <PhoneCardActionRow
        user_profile={user_profile}
        setStage={setStage}
        setSubscribeDialogOpen={setSubscribeDialogOpen}
      />
    );
  };

  return (
    <>
      <CardBase
        icon={
          <SvgIcon component={PhoneIcon} sx={{width: "32px", height: "32px"}} />
        }
        title={
          <FlexContainer alignItems="center" gap={theme.spacing(3)}>
            <Text variant="textLG">Phone</Text>
            <StatusChip
              skeleton={{
                enabled: userProfileLoading,
                width: "150px",
              }}
              text={isSmsEnabled ? "Enabled" : "Not Enabled"}
              type={isSmsEnabled ? "success" : "error"}
            />
          </FlexContainer>
        }
        description={
          <Text variant="textSM" sx={{color: theme.colors.textSecondary}}>
            {t("managed-phone-business-setting-info")}
          </Text>
        }
        inlineContent={getCardContent()}
        actionRow={getActionRow()}
        {...props}
      />
      <ServiceInfoDialog
        open={subscribeDialogOpen}
        service={addonServices.twilio[0]}
        handleEnable={subscribe}
        loading={subscriptionLoading}
        onClose={() => setSubscribeDialogOpen(false)}
        btnLabel="Subscribe"
      />
    </>
  );
};

export default PhoneCard;
